import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useRouteContext } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import type { audience } from '@bb/contracts';
import { Button, ConfirmDialog, ErrorState, ReadOnlyBanner } from '@bb/ui';
import { ApiError, type SuppressionListView } from '../../api/client.ts';
import { usePermissions } from '../access/usePermissions.ts';
import { AudienceBuilder } from '../audience/AudienceBuilder.tsx';
import { AudiencePopulation } from '../audience/AudiencePopulation.tsx';
import { EditorShell } from '../editor/EditorShell.tsx';
import { useEditorState } from '../editor/useEditorState.ts';
import { TagChecklist } from './TagChecklist.tsx';

interface Draft {
  name: string;
  description: string;
  definition: audience.AudienceDefinition;
  exceptionTagIds: string[];
}
const draftOf = (l: SuppressionListView): Draft => ({ name: l.name, description: l.description ?? '', definition: l.definition as unknown as audience.AudienceDefinition, exceptionTagIds: l.exceptionTagIds });
const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');

/** One suppression list (BB-49): its audience, size, exception tags; activation applies to every message at once. */
export function SuppressionListPage() {
  const { environmentId, listId } = useParams({ from: '/env/$environmentId/settings/suppression-lists/$listId' });
  const { api } = useRouteContext({ from: '/env/$environmentId/settings/suppression-lists/$listId' });
  const queryClient = useQueryClient();
  const { has } = usePermissions();
  const canEdit = has('env.manage');
  const key = ['suppression-list', environmentId, listId];
  const list = useQuery({ queryKey: key, queryFn: () => api.suppressionList(environmentId, listId) });
  const server = useMemo(() => (list.data ? draftOf(list.data) : undefined), [list.data]);
  const editor = useEditorState(server);
  const [confirming, setConfirming] = useState(false);
  const done = (row: SuppressionListView) => {
    queryClient.setQueryData(key, row);
    void queryClient.invalidateQueries({ queryKey: ['suppression-lists', environmentId] });
  };
  const save = useMutation({
    mutationFn: (d: Draft) => api.updateSuppressionList(environmentId, listId, { name: d.name.trim(), description: d.description.trim() || null, definition: d.definition, exceptionTagIds: d.exceptionTagIds }),
    onSuccess: (row) => {
      editor.markSaved(draftOf(row));
      done(row);
    },
  });
  const toggle = useMutation({ mutationFn: (active: boolean) => api.setSuppressionListActive(environmentId, listId, active), onSuccess: done });
  const archive = useMutation({ mutationFn: () => api.archiveSuppressionList(environmentId, listId), onSuccess: done });

  if (list.isError) return <ErrorState title="The list could not be loaded" message={errorText(list.error)} onRetry={() => void list.refetch()} />;
  const draft = editor.draft;
  if (!list.data || !draft) return <p aria-busy="true">Loading…</p>;
  const l = list.data;
  const active = l.status === 'active';
  const readOnly = !canEdit || Boolean(l.archivedAt);

  return (
    <EditorShell dirty={editor.isDirty} onSave={() => save.mutateAsync(draft)} canSave={!readOnly && draft.name.trim() !== ''} objectLabel="suppression list">
      <section className="suppression-list">
        <h1>{l.name}</h1>
        <p>
          {active ? <span className="tag">Active since {l.activatedAt?.slice(0, 16).replace('T', ' ')} UTC</span> : <span className="tag tag--neutral">Inactive</span>}
          {l.archivedAt && <span className="tag tag--neutral"> Archived</span>}
        </p>
        {!canEdit && <ReadOnlyBanner>Suppression lists are changed by admins (env.manage).</ReadOnlyBanner>}
        {active && <p className="notice notice--warning">Active: changes apply to every campaign and Canvas as soon as they are saved.</p>}
        <div className="stack-form">
          <label>
            Name
            <input value={draft.name} disabled={readOnly} onChange={(e) => editor.update({ name: e.target.value })} />
          </label>
          <label>
            Description
            <textarea value={draft.description} disabled={readOnly} onChange={(e) => editor.update({ description: e.target.value })} />
          </label>
        </div>
        <AudienceBuilder environmentId={environmentId} value={draft.definition} readOnly={readOnly} onChange={(definition) => editor.update({ definition })} />
        <AudiencePopulation environmentId={environmentId} definition={draft.definition} />
        <TagChecklist environmentId={environmentId} label="Exception tags: messages with any of these still reach the list" value={draft.exceptionTagIds} disabled={readOnly} onChange={(exceptionTagIds) => editor.update({ exceptionTagIds })} />
        {!readOnly && (
          <div role="group" aria-label="List actions" className="filters">
            <Button onClick={() => save.mutate(draft)} disabled={!editor.isDirty || save.isPending || !draft.name.trim()}>
              Save
            </Button>
            {active ? (
              <Button variant="ghost" onClick={() => toggle.mutate(false)} disabled={toggle.isPending}>
                Deactivate
              </Button>
            ) : (
              <>
                <Button variant="danger" onClick={() => setConfirming(true)} disabled={editor.isDirty || toggle.isPending}>
                  Activate
                </Button>
                <Button variant="ghost" onClick={() => archive.mutate()} disabled={archive.isPending}>
                  Archive
                </Button>
              </>
            )}
          </div>
        )}
        {(save.isError || toggle.isError || archive.isError) && <p role="alert">{errorText(save.error ?? toggle.error ?? archive.error)}</p>}
        <ConfirmDialog
          open={confirming}
          onOpenChange={setConfirming}
          title={`Activate «${l.name}»?`}
          confirmLabel="Activate"
          busy={toggle.isPending}
          onConfirm={() => toggle.mutate(true, { onSuccess: () => setConfirming(false) })}
        >
          Matching users stop receiving every campaign and Canvas message right away, except messages with an exception tag and transactional ones.
        </ConfirmDialog>
      </section>
    </EditorShell>
  );
}
